// My actions: every next action one person owns across the projects the daily run works,
// led by each project's Manager's ranking. Opens on the viewer's own list; the name in
// the heading switches to a teammate's. See docs/Y2026-Q4/design-my-actions.md.
import { useContext, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { useDashboard } from "../api/hooks/useDashboard";
import { useDashboardActions } from "../api/hooks/useDashboardActions";
import { useOpenFeedback } from "../api/hooks/useAgentFeedback";
import { useUnreadAnswers } from "../api/hooks/useUnreadAnswers";
import { UserContext } from "../auth";
import { CountBadge } from "../components/CountBadge";
import { ActionTable } from "../components/NextActionTable";
import { ReviewProvider } from "../components/ReviewProvider";
import { PageHeader } from "../components/PageHeader";
import { useOtherSlotsInUse } from "../api/hooks/useAgentConfigs";
import { DEFAULT_ONLY_NOTE } from "../utils/agentSlotText";
import { PersonSwitcher } from "../components/PersonSwitcher";
import { pageTitleClassName } from "../components/pageTitleClassName";
import { OpenAgentFeedback } from "../components/OpenAgentFeedback";
import { UnreadAnswers } from "../components/UnreadAnswers";
import {
  type ActionSource,
  managerRanking,
  onWhom,
} from "../utils/nextActions";
import { useDocumentTitle } from "../utils/useDocumentTitle";
import { useScrollToHash } from "../utils/useScrollToHash";
import styles from "./MyActionsRoute.module.css";

const PERSON_PARAM = "person";
// Which tab: absent is Actions. Open agent feedback and Unread answers sit on the viewer's
// own list only: the first is the team's, the same on everyone's, the second is theirs.
const VIEW_PARAM = "view";
const FEEDBACK_VIEW = "feedback";
const UNREAD_VIEW = "unread";

export const MyActionsRoute = () => {
  const { user } = useContext(UserContext);
  const viewer = (user?.email ?? "").toLowerCase();
  const otherSlots = useOtherSlotsInUse();
  const [params, setParams] = useSearchParams();
  const person = (params.get(PERSON_PARAM) || viewer).toLowerCase();
  // The landing page's own query: it holds each project's Manager's ranking, and says
  // while an agent is taking in a change, which the actions reload to follow.
  const dashboard = useDashboard();
  const actions = useDashboardActions(
    !!dashboard.data?.projects.some((p) => p.applying_review),
  );

  // Keyed by lower-case email, as the viewer and person are compared.
  const team = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(actions.data?.team ?? {}).map(([email, label]) => [
          email.toLowerCase(),
          label,
        ]),
      ),
    [actions.data],
  );
  const name = team[person] ?? person;
  const mine = person === viewer;
  const view = mine ? params.get(VIEW_PARAM) : null;
  const unreadView = view === UNREAD_VIEW;
  const feedbackView = view === FEEDBACK_VIEW;
  const actionsView = !unreadView && !feedbackView;
  // Matched against the sender the conversations recorded, so as the sign-in gives it.
  const unread = useUnreadAnswers(user?.email ?? "", mine);
  const openFeedback = useOpenFeedback(mine, viewer);
  useDocumentTitle(mine ? "My actions" : `${name}'s actions`);
  useScrollToHash(!!actions.data && !dashboard.isLoading);

  const sources: ActionSource[] = useMemo(() => {
    const data = actions.data;
    if (!data) return [];
    const rows = new Map(
      (dashboard.data?.projects ?? []).map((row) => [row.project_id, row]),
    );
    return data.projects.map((project) => ({
      projectId: project.project_id,
      projectName: project.name,
      data: {
        ...project,
        team: data.team,
        category_names: data.category_names,
        lists_today: data.today,
        applying_review: rows.get(project.project_id)?.applying_review ?? null,
      },
      manager: managerRanking(rows.get(project.project_id), { picksOnly: true }),
    }));
  }, [actions.data, dashboard.data]);

  // What the switcher shows beside each person: their open actions on them now.
  const onThem = useMemo(() => {
    const counts = new Map<string, number>();
    for (const source of sources)
      for (const entry of source.data.agents)
        for (const action of entry.lists?.actions ?? []) {
          if (action.closed || onWhom(action).side !== "us") continue;
          if (source.data.action_dismissals?.[action.action_id]) continue;
          const owner = action.internal_owner.toLowerCase();
          counts.set(owner, (counts.get(owner) ?? 0) + 1);
        }
    return counts;
  }, [sources]);

  const choose = (email: string) => {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (email === viewer) next.delete(PERSON_PARAM);
        else {
          next.set(PERSON_PARAM, email);
          next.delete(VIEW_PARAM);
        }
        return next;
      },
      { replace: false },
    );
  };

  const showView = (view: string | null) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (view) next.set(VIEW_PARAM, view);
        else next.delete(VIEW_PARAM);
        return next;
      },
      { replace: true },
    );

  // The other tabs read none of the actions, so they do not wait on them. A cached list
  // from an earlier visit is not shown: what has moved since the page opened is measured
  // from the first list it shows, which must be a fresh read.
  if (actionsView && (!actions.isFetchedAfterMount || dashboard.isLoading))
    return <p className={styles.muted}>Loading the actions…</p>;
  if (actionsView && (actions.error || !actions.data))
    return (
      <p className={styles.error}>
        Could not load the actions. Reload to try again.
      </p>
    );

  const people = Object.entries(team).sort(([a], [b]) =>
    a === viewer ? -1 : b === viewer ? 1 : team[a].localeCompare(team[b]),
  );

  // The menu sits beside the h1 rather than inside it, so it keeps its own type and
  // stays out of the heading's accessible name.
  const picker = (
    <PersonSwitcher
      title={mine && !team[viewer] ? "My actions" : `${name}'s actions`}
      sections={[
        people.map(([email, label]) => ({
          key: email,
          label: `${label}${email === viewer ? " (you)" : ""}`,
          count: `${onThem.get(email) ?? 0} on ${email === viewer ? "you" : "them"}`,
        })),
      ]}
      current={person}
      onChoose={choose}
      wrap={(button) => <h1 className={pageTitleClassName}>{button}</h1>}
    />
  );

  return (
    <>
      <PageHeader
        heading={picker}
        description={otherSlots && DEFAULT_ONLY_NOTE}
        beside={
          !mine && (
            <span className={styles.viewing}>
              Viewing {name}&apos;s list{" "}
              <button
                type="button"
                className={styles.back}
                onClick={() => choose(viewer)}
              >
                Back to mine
              </button>
            </span>
          )
        }
      />
      <div className={styles.page}>
        {mine && actions.data && !team[viewer] && (
          <p className={styles.error}>
            Your sign-in email, {viewer || "unknown"}, is not on the team list,
            so no actions can be assigned to it. Pick a person above to see
            their list.
          </p>
        )}
        {mine && (
          <div className={styles.tabs} role="tablist" aria-label="View">
            <button
              type="button"
              role="tab"
              aria-selected={actionsView}
              className={`${styles.tab} ${actionsView ? styles.tabActive : ""}`}
              onClick={() => showView(null)}
            >
              Actions
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={feedbackView}
              className={`${styles.tab} ${feedbackView ? styles.tabActive : ""}`}
              onClick={() => showView(FEEDBACK_VIEW)}
            >
              Open agent feedback
              <CountBadge count={openFeedback.waiting} />
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={unreadView}
              className={`${styles.tab} ${unreadView ? styles.tabActive : ""}`}
              onClick={() => showView(UNREAD_VIEW)}
            >
              Unread answers
              <CountBadge count={unread.data?.unread.length ?? 0} outline />
            </button>
          </div>
        )}
        {feedbackView ? (
          <OpenAgentFeedback viewer={viewer} />
        ) : unreadView ? (
          <UnreadAnswers
            data={unread.data}
            error={unread.error instanceof Error ? unread.error : null}
            isLoading={unread.isLoading}
            email={user?.email ?? ""}
          />
        ) : (
          <div className={styles.card}>
            {dashboard.error ? (
              <p className={styles.error}>
                Could not load the Manager&apos;s rankings, so Priority orders
                by due date alone. Reload to try again.
              </p>
            ) : (
              <p className={styles.muted}>
                Every action {mine ? "you own" : `${name} owns`} across the
                projects the daily run works. Priority follows each
                project&apos;s Manager ranking, then the due date.
              </p>
            )}
            {/* Each row names its own project (NextActionTable), so text selected in one
                is a comment on that project's action. */}
            <ReviewProvider
              available
              defaultDoc={null}
              defaultLevel="specialist_details"
              comments={[]}
              projectId={null}
              onQuote={null}
            >
              <ActionTable
                // A different person starts from the default view again.
                key={person}
                scope={{ kind: "person", email: person, name, viewer: mine }}
                sources={sources}
                empty={
                  mine
                    ? "No actions are assigned to you on the projects the daily run works."
                    : `No actions are assigned to ${name} on the projects the daily run works.`
                }
                controls
                showArea={false}
                anchored
              />
            </ReviewProvider>
          </div>
        )}
      </div>
    </>
  );
};
